import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/pages/Companies";
import { toast } from "sonner";
import { Landmark, Pencil, Loader2 } from "lucide-react";

export default function BankDetailsCard() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);

  const { data: bank } = useQuery({ queryKey: ["bank-details"], queryFn: async () => (await api.get("/settings/bank")).data });

  const save = useMutation({
    mutationFn: async () => (await api.put("/settings/bank", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["bank-details"] }); setOpen(false); toast.success("Bank details updated"); },
    onError: (e) => toast.error(e?.response?.status === 403 ? "Only a super admin can change bank details" : e?.response?.data?.detail || "Update failed"),
  });

  if (!bank) return null;
  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow p-5" data-testid="bank-details-card">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 text-emerald-700 shrink-0">
          <Landmark size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-semibold text-slate-900">Client payment — bank transfer details</div>
          <p className="text-sm text-slate-600 mb-2">Shown to clients on the portal after they accept a quote. Bank transfer only — no card payments.</p>
          <div className="grid sm:grid-cols-3 gap-2 text-xs">
            <div className="border border-slate-200 bg-slate-50 rounded-sm p-2"><div className="stamp-label text-slate-500">Account name</div><div className="font-semibold text-slate-900" data-testid="bank-acc-name">{bank.account_name}</div></div>
            <div className="border border-slate-200 bg-slate-50 rounded-sm p-2"><div className="stamp-label text-slate-500">BSB</div><div className="font-mono font-semibold text-slate-900" data-testid="bank-bsb">{bank.bsb}</div></div>
            <div className="border border-slate-200 bg-slate-50 rounded-sm p-2"><div className="stamp-label text-slate-500">Account number</div><div className="font-mono font-semibold text-slate-900" data-testid="bank-acc-no">{bank.account_number}</div></div>
          </div>
          <div className="text-[10px] text-slate-500 mt-2">Payment reference shown to clients: their customer number (e.g. CCFI-80101) + quote number.</div>
        </div>
        <Button data-testid="bank-edit-btn" size="sm" variant="outline" onClick={() => { setForm({ ...bank }); setOpen(true); }} className="rounded-sm shrink-0">
          <Pencil size={13} className="mr-1.5" /> Edit
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="bank-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Edit bank details</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-sm">Super admin only. These details are shown to clients — double-check before saving.</p>
          <div className="space-y-3">
            <Field label="Account name" required><Input data-testid="bank-form-name" value={form?.account_name || ""} onChange={(e) => setForm({ ...form, account_name: e.target.value })} /></Field>
            <Field label="BSB" required><Input data-testid="bank-form-bsb" value={form?.bsb || ""} onChange={(e) => setForm({ ...form, bsb: e.target.value })} /></Field>
            <Field label="Account number" required><Input data-testid="bank-form-accno" value={form?.account_number || ""} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="bank-form-save-btn" onClick={() => save.mutate()} disabled={save.isPending} className="rounded-sm bg-emerald-600 hover:bg-emerald-700">
              {save.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Save details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
